import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCourses, fetchDriveCourses } from "./features/courses/coursesSlice";
import { fetchComments } from "./features/comments/commentsSlice";
import { setCurrentUser, selectCurrentUser, addGoogleAccount } from "./features/user/userSlice";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import HomePage from "./pages/HomePage";
import ExternalRedirect from "./components/ExternalRedirect";
import BottomText from "./components/BottomText";
import Theme from "./components/ChangeTheme";
import SeoMetadata from "./components/SeoMetadata";
import UserLoginForm from "./features/user/UserLoginForm";
// import LlmTelemetryDebugPanel from "./components/LlmTelemetryDebugPanel";
import { onAuthStateChanged, getRedirectResult } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./app/firebase.config";
import { getGoogleDriveStatus } from "./utils/googleDriveAPI";
import {
  fetchCanvasIntegrationStatus,
  syncCanvasCourses,
  syncCanvasAssignments,
  syncCanvasSubmissions,
} from "./utils/lessonPlanApi";
import {
  MISSING_VOICES_PATH,
  getMissingVoicesUrl,
  isLocalDevelopmentHost,
  isMissingVoicesHost,
} from "./utils/missingVoicesRouting";
import { getPrivateRouteSeo, getSeoImageUrl } from "./utils/seo";
import { PRODUCT_ANALYTICS_EVENTS } from "./utils/analyticsEvents";
import {
  getAnalyticsConsent,
  identifyAnalyticsUser,
  initializeSourceAttributionFromLocation,
  resetAnalyticsUser,
  trackProductEvent,
} from "./utils/productAnalytics";
import "./App.css";
import "./styles/slick.css";

const CoursesDirectoryPage = lazy(() => import("./pages/CoursesDirectoryPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const StudentDetailPage = lazy(() => import("./pages/StudentDetailPage"));
const MissingPerspectivePage = lazy(() => import("./pages/MissingPerspectivePage"));
const ProfileSettingsPage = lazy(() => import("./pages/ProfileSettingsPage"));
const CourseDetailPage = lazy(() => import("./pages/CourseDetailPage"));
const WarmUpPage = lazy(() => import("./pages/WarmUpPage"));
const CurriculumLibraryPage = lazy(() => import("./pages/CurriculumLibraryPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const VideosPage = lazy(() => import("./pages/VideosPage"));
const AnalyticsDashboard = lazy(() => import("./components/AnalyticsDashboard"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const BackwardDesignWizard = lazy(() => import("./components/BackwardDesignWizard"));
const LessonPlanWorkspace = lazy(() => import("./components/LessonPlanWorkspace"));
const CredibleShowcasePage = lazy(() => import("./pages/CredibleShowcasePage"));

const RouteLoadingFallback = () => (
  <div className="d-flex justify-content-center py-5 text-muted">
    Loading...
  </div>
);

const getEmailDomain = (email) => {
  if (typeof email !== "string" || !email.includes("@")) return null;
  return email.split("@")[1]?.toLowerCase() || null;
};

const getAppSurfaceClass = ({ pathname, isMissingVoicesDomain }) => {
  if (pathname === "/") {
    return isMissingVoicesDomain
      ? "app-surface-missing-perspective"
      : "app-surface-home";
  }

  if (
    pathname === MISSING_VOICES_PATH ||
    pathname === "/missing-voices" ||
    pathname === "/missing-perspectives"
  ) {
    return "app-surface-missing-perspective";
  }

  if (pathname === "/videos" || pathname.startsWith("/videos/")) {
    return "app-surface-videos";
  }

  return "";
};

// ARCHIVED CONTENT REFERENCE (commit: 21a9e1b):
// - TheTeachersLounge component: src/pages/TheTeachersLounge.js
// - RantPage component: src/pages/RantPage.js
// Build: v2025.11.19.01
// - TakesPage component: src/pages/TakesPage.js
// - MovieDayPage component: src/pages/MovieDayPage.js
// - fetchRants: src/features/rants/rantsSlice.js
// - fetchTakes: src/features/takes/takesSlice.js
// - Old Header/VaporWaveHeader with full navigation menu
// To restore these features, check out commit 21a9e1b

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const { hash, pathname } = useLocation();
  const hostname = window.location.hostname;
  const layoutClass =
    pathname.startsWith("/mydata") ||
    pathname.startsWith("/gradebook-demo") ||
    pathname.startsWith("/credible")
    ? "layout-compact"
    : "layout-spacious";
  const [isCheckingRedirect, setIsCheckingRedirect] = useState(true);
  const redirectHandledRef = useRef(false);
  const authBootstrapUidRef = useRef(null);
  const profileSyncUidRef = useRef(null);
  const publicCoursesRequestedRef = useRef(false);
  const canvasAutoSyncRef = useRef({
    uid: null,
    inFlight: false,
    lastAttemptAt: 0,
  });
  const isLocalhost = isLocalDevelopmentHost(hostname);
  const isMissingVoicesDomain = isMissingVoicesHost(hostname);
  const missingVoicesUrl = getMissingVoicesUrl(MISSING_VOICES_PATH);
  const privateRouteSeo = getPrivateRouteSeo(pathname);
  const appSurfaceClass = getAppSurfaceClass({
    pathname,
    isMissingVoicesDomain,
  });
  // const showLlmDebugPanel =
  //   isLocalhost || process.env.REACT_APP_SHOW_LLM_DEBUG_PANEL === "true";

  useEffect(() => {
    initializeSourceAttributionFromLocation(window.location.search);
    trackProductEvent(PRODUCT_ANALYTICS_EVENTS.PAGE_VIEWED, {
      path: pathname,
      hash_present: Boolean(hash),
      host: hostname,
      is_missing_voices_domain: isMissingVoicesDomain,
    });
  }, [hash, hostname, isMissingVoicesDomain, pathname]);

  useEffect(() => {
    // Public course catalog can load without authentication.
    if (publicCoursesRequestedRef.current) {
      return;
    }
    publicCoursesRequestedRef.current = true;
    dispatch(fetchCourses());
  }, [dispatch]);

  useEffect(() => {
    if (isCheckingRedirect) return;
    const uid = currentUser?.uid || null;
    if (!uid) {
      authBootstrapUidRef.current = null;
      return;
    }
    if (authBootstrapUidRef.current === uid) {
      return;
    }
    authBootstrapUidRef.current = uid;
    dispatch(fetchComments());
    dispatch(fetchDriveCourses());
  }, [dispatch, currentUser?.uid, isCheckingRedirect]);

  // Handle Google redirect result - must run BEFORE onAuthStateChanged
  useEffect(() => {
    if (redirectHandledRef.current) {
      setIsCheckingRedirect(false);
      return;
    }
    redirectHandledRef.current = true;

    const handleRedirectResult = async () => {
      try {
        console.log("🔍 Checking for Google redirect result...");

        // Wait briefly for Firebase to initialize auth state (prevents race condition)
        await new Promise(resolve => setTimeout(resolve, 100));

        const result = await getRedirectResult(auth);

        if (result) {
          const user = result.user;

          console.log("✅ Google sign-in successful:", {
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
          });

          // Drive permissions are connected explicitly from Account settings.
          dispatch(
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
              emailVerified: user.emailVerified,
              googleAccessToken: null,
              driveEnabled: false,
            })
          );
        } else {
          console.log("ℹ️ No redirect result found");
        }
      } catch (error) {
        console.error("❌ Redirect result error:", error);

        // Handle specific error cases
        if (error.code === 'auth/popup-closed-by-user') {
          console.log("ℹ️ User closed OAuth popup");
        } else if (error.code === 'auth/unauthorized-domain') {
          console.error("❌ Domain not authorized in Firebase Console");
        } else if (error.code === "auth/invalid-credential") {
          console.error(
            "❌ Redirect credential was rejected. This can happen when a stale auth redirect URL is cached."
          );
        } else if (error.message?.includes('COOP')) {
          console.error("❌ Cross-Origin-Opener-Policy issue - using redirect flow");
        }
      } finally {
        sessionStorage.removeItem("firebase_redirect_nonce");
        const cleanedUrl = new URL(window.location.href);
        if (cleanedUrl.searchParams.has("authRedirect")) {
          cleanedUrl.searchParams.delete("authRedirect");
          window.history.replaceState(
            {},
            document.title,
            `${cleanedUrl.pathname}${cleanedUrl.search}${cleanedUrl.hash}`
          );
        }

        // Signal that redirect check is complete
        setIsCheckingRedirect(false);
      }
    };

    handleRedirectResult();
  }, [dispatch]);

  // Listen for Firebase auth state changes and sync with Redux
  // Wait for redirect check to complete before subscribing to avoid race condition
  useEffect(() => {
    // Don't subscribe until redirect check is complete
    if (isCheckingRedirect) {
      console.log("⏳ Waiting for redirect check to complete before subscribing to auth changes");
      return;
    }

    console.log("🎧 Subscribing to Firebase auth state changes");
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log("🔄 Auth state changed - user logged in:", user.email);

        const syncUserProfile = async () => {
          if (!user?.uid) return;

          const userRef = doc(db, "users", user.uid);
          if (profileSyncUidRef.current === user.uid) {
            try {
              const userSnap = await getDoc(userRef);
              return {
                profileData: userSnap.exists() ? userSnap.data() : null,
                isNewUser: false,
              };
            } catch (error) {
              console.warn("Failed to read user profile:", error);
              return {
                profileData: null,
                isNewUser: false,
              };
            }
          }
          profileSyncUidRef.current = user.uid;
          const consentValue = getAnalyticsConsent();

          try {
            const userSnap = await getDoc(userRef);
            let isNewUser = false;
            if (!userSnap.exists()) {
              isNewUser = true;
              const payload = {
                createdAt: serverTimestamp(),
                lastSeenAt: serverTimestamp(),
              };
              payload.analyticsConsent = consentValue;
              await setDoc(userRef, payload, { merge: true });
            } else {
              const updates = {
                lastSeenAt: serverTimestamp(),
              };
              if (typeof userSnap.data()?.analyticsConsent !== "boolean") {
                updates.analyticsConsent = consentValue;
              }
              await setDoc(userRef, updates, { merge: true });
            }
            return {
              profileData: userSnap.exists() ? userSnap.data() : null,
              isNewUser,
            };
          } catch (error) {
            console.warn("Failed to sync user profile:", error);
            return {
              profileData: null,
              isNewUser: false,
            };
          }
        };

        // Check if this user signed in with Google and might have Drive access
        const providerData = user.providerData.find(
          provider => provider.providerId === "google.com"
        );

        // Try to retrieve access token from localStorage (persisted from previous session)
        let googleAccessToken = null;
        if (providerData) {
          googleAccessToken = localStorage.getItem('google_access_token');
          if (googleAccessToken) {
            console.log("♻️ Retrieved access token from localStorage");

            // Check token age - tokens expire after 1 hour
            const tokenTimestamp = localStorage.getItem('google_access_token_timestamp');
            if (tokenTimestamp) {
              const tokenAge = Date.now() - parseInt(tokenTimestamp);
              const oneHour = 60 * 60 * 1000;

              if (tokenAge > oneHour) {
                console.warn("⚠️ Access token is older than 1 hour, may be expired");
                // Don't clear it yet - let Drive API return error if expired
                // User will need to re-authenticate
              } else {
                // Restore Google account to Redux if token is still valid
                dispatch(addGoogleAccount({
                  email: user.email,
                  displayName: user.displayName,
                  photoURL: user.photoURL,
                  accessToken: googleAccessToken,
                  tokenTimestamp: parseInt(tokenTimestamp),
                  // Note: folder info not persisted to localStorage, will be fetched on next Drive operation
                }));
              }
            }
          } else {
            console.log("ℹ️ Google sign-in detected. Connect Drive from Account when needed.");
          }
        }

        const syncDriveStatus = async () => {
          const { profileData: userProfileData, isNewUser } =
            (await syncUserProfile()) || {};
          let driveConnected = false;
          try {
            const status = await getGoogleDriveStatus();
            driveConnected = status?.connected === true;
          } catch (error) {
            console.warn("Drive status check failed:", error?.message || error);
          }

          const userData = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            preferredName:
              typeof userProfileData?.preferredName === "string"
                ? userProfileData.preferredName
                : null,
            photoURL: user.photoURL,
            emailVerified: user.emailVerified,
            driveEnabled: driveConnected || (!!providerData && !!googleAccessToken),
            googleAccessToken: googleAccessToken,
          };

          console.log("📤 Dispatching user to Redux:", {
            ...userData,
            googleAccessToken: userData.googleAccessToken ? "[REDACTED]" : null,
          });

          dispatch(setCurrentUser(userData));
          identifyAnalyticsUser(user.uid, {
            auth_method: providerData ? "google" : "email_password",
            drive_enabled: userData.driveEnabled,
            email_domain: getEmailDomain(user.email),
            email_verified: Boolean(user.emailVerified),
            is_missing_voices_domain: isMissingVoicesDomain,
          });
          trackProductEvent(
            isNewUser
              ? PRODUCT_ANALYTICS_EVENTS.SIGNUP_COMPLETED
              : PRODUCT_ANALYTICS_EVENTS.LOGIN_COMPLETED,
            {
              auth_method: providerData ? "google" : "email_password",
              drive_enabled: userData.driveEnabled,
              email_verified: Boolean(user.emailVerified),
            }
          );

          if (driveConnected) {
            dispatch(
              addGoogleAccount({
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                accessToken: googleAccessToken || null,
                tokenTimestamp: googleAccessToken ? Date.now() : null,
              })
            );
          }
        };

        syncDriveStatus();

        const syncCanvasOnLogin = async () => {
          const uid = user.uid;
          const now = Date.now();
          const lastSync = Number(localStorage.getItem("canvas_last_sync") || 0);
          if (lastSync && now - lastSync < 5 * 60 * 1000) {
            return;
          }
          if (
            canvasAutoSyncRef.current.uid === uid &&
            (canvasAutoSyncRef.current.inFlight ||
              now - canvasAutoSyncRef.current.lastAttemptAt < 30 * 1000)
          ) {
            return;
          }
          canvasAutoSyncRef.current = {
            uid,
            inFlight: true,
            lastAttemptAt: now,
          };
          let status = null;
          try {
            status = await fetchCanvasIntegrationStatus();
          } catch (error) {
            console.warn("Canvas status check failed:", error?.message || error);
            return;
          }
          if (!status?.connected) return;
          try {
            await syncCanvasCourses();
            await syncCanvasAssignments();
            if (!isLocalhost) {
              await syncCanvasSubmissions({ skipGraded: false, totalsOnly: true });
            }
            localStorage.setItem("canvas_last_sync", String(Date.now()));
          } catch (error) {
            console.warn("Canvas auto-sync failed:", error?.message || error);
          } finally {
            canvasAutoSyncRef.current = {
              uid,
              inFlight: false,
              lastAttemptAt: now,
            };
          }
        };

        syncCanvasOnLogin();

        const shouldPromptCanvas = localStorage.getItem('canvas_connect_prompt') === '1';
        if (shouldPromptCanvas && window.location.pathname !== '/mydata') {
          localStorage.removeItem('canvas_connect_prompt');
          navigate('/mydata#canvas');
        }

        // Force a small delay to ensure Redux has updated before UI checks
        setTimeout(() => {
          console.log("✅ UI should now reflect logged-in state");
        }, 50);
      } else {
        console.log("🔄 Auth state changed - user logged out");

        // Clear access token from localStorage on logout
        localStorage.removeItem('google_access_token');
        localStorage.removeItem('google_access_token_timestamp');
        localStorage.removeItem('google_access_token_expires_at');
        localStorage.removeItem('google_drive_connected');
        canvasAutoSyncRef.current = {
          uid: null,
          inFlight: false,
          lastAttemptAt: 0,
        };

        dispatch(setCurrentUser(null));
        resetAnalyticsUser();
        profileSyncUidRef.current = null;
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe && unsubscribe();
  }, [dispatch, navigate, isCheckingRedirect, isLocalhost, isMissingVoicesDomain]);

  useEffect(() => {
    if (hash) {
      if (hash === "#TeachersLounge") {
        const element = document.getElementById("TeachersLounge");
        if (element) {
          // Temporarily remove the smooth scroll behavior
          element.scrollIntoView({ behavior: "auto" });
          // Add back the smooth scroll behavior after navigation
          setTimeout(() => {
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname + window.location.search
            ); // Clear the hash to prevent scrolling
          }, 0);
        }
      } else {
        const element = document.getElementById(hash.replace("#", ""));
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  }, [hash]);

  return (
    <div className={`App ${layoutClass} ${appSurfaceClass}`.trim()}>
      <Theme />
      <UserLoginForm showButton={false} />
      {privateRouteSeo ? (
        <SeoMetadata
          title={privateRouteSeo.title}
          description={privateRouteSeo.description}
          canonicalUrl={privateRouteSeo.canonicalUrl}
          robots={privateRouteSeo.robots}
          openGraph={{
            title: privateRouteSeo.title,
            description: privateRouteSeo.description,
            url: privateRouteSeo.canonicalUrl,
            image: getSeoImageUrl(),
            imageAlt: "Teach League private app poster",
          }}
          twitter={{
            title: privateRouteSeo.title,
            description: privateRouteSeo.description,
            image: getSeoImageUrl(),
            imageAlt: "Teach League private app poster",
          }}
        />
      ) : null}
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route
            path="/"
            element={isMissingVoicesDomain ? <MissingPerspectivePage /> : <HomePage />}
          />
          <Route
            path="missingvoices"
            element={
              isMissingVoicesDomain || isLocalhost ? (
                <MissingPerspectivePage />
              ) : (
                <ExternalRedirect to={missingVoicesUrl} />
              )
            }
          />
          <Route
            path="missing-voices"
            element={
              isMissingVoicesDomain || isLocalhost ? (
                <Navigate to={MISSING_VOICES_PATH} replace />
              ) : (
                <ExternalRedirect to={missingVoicesUrl} />
              )
            }
          />
          <Route
            path="missing-perspectives"
            element={
              isMissingVoicesDomain || isLocalhost ? (
                <Navigate to={MISSING_VOICES_PATH} replace />
              ) : (
                <ExternalRedirect to={missingVoicesUrl} />
              )
            }
          />
          <Route path="wizard" element={<BackwardDesignWizard />} />
          <Route path="workspace" element={<LessonPlanWorkspace />} />
          <Route path="plans/:planId" element={<LessonPlanWorkspace />} />
          {/* Redirect to external personal website */}
          <Route path="lounge" element={<ExternalRedirect to="https://eitans.website" />} />
          <Route path="TheTeachersLounge" element={<ExternalRedirect to="https://eitans.website" />} />
          {/* Shareable link to the group project's Gemini conversation (Gemini blocks iframes, so redirect) */}
          <Route
            path="group-project"
            element={<ExternalRedirect to="https://gemini.google.com/share/d/1Af2wrsv8mbQruOjl499KRLnzht46H4NY" />}
          />
          <Route path="project" element={<Navigate to="/group-project" replace />} />
          {/* User account management - preserved for future CRUD operations */}
          {/* Public Credible showcase — full insight + action layer, no auth. */}
          <Route
            path="credible/gradebook-demo"
            element={<CredibleShowcasePage />}
          />
          <Route
            path="credible/gradebook-demo/students/:studentId"
            element={<StudentDetailPage mode="credibleDemo" />}
          />
          <Route
            path="credible"
            element={<Navigate to="/credible/gradebook-demo" replace />}
          />

          {/* Plain public-safe gradebook demo (no Credible integration). */}
          <Route
            path="gradebook-demo"
            element={<AccountPage mode="publicDemo" />}
          />
          <Route
            path="gradebook-demo/students/:studentId"
            element={<StudentDetailPage mode="publicDemo" />}
          />
          <Route path="demo" element={<Navigate to="/gradebook-demo" replace />} />
          <Route path="mydata" element={<AccountPage />} />
          <Route path="dashboard" element={<Navigate to="/mydata" replace />} />
          <Route path="mydata/students/:studentId" element={<StudentDetailPage />} />
          <Route path="account" element={<Navigate to="/mydata" replace />} />
          <Route path="profile" element={<ProfileSettingsPage />} />
          <Route path="directory" element={<CoursesDirectoryPage />} />
          <Route path="directory/:courseId" element={<CourseDetailPage />} />
          <Route path="curriculum" element={<CurriculumLibraryPage />} />
          <Route path="warmups" element={<WarmUpPage />} />
          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="terms-of-service" element={<TermsOfServicePage />} />
          <Route path="terms" element={<Navigate to="/terms-of-service" replace />} />
          <Route
            path="videos"
            element={<VideosPage />}
          />
          {/* Adaptive Lesson Planning System */}
          <Route path="assignments" element={<Navigate to="/mydata" replace />} />
          <Route path="analytics" element={<AnalyticsDashboard />} />
          <Route path="admin" element={<AdminDashboardPage />} />
        </Routes>
      </Suspense>
      {/* {showLlmDebugPanel && <LlmTelemetryDebugPanel />} */}
      <BottomText />
    </div>
  );
}

export default App;
